import { Box } from "@mui/material";
import {
  createLink,
  createFileRoute,
  Outlet,
  useLocation,
} from "@tanstack/react-router";
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";

import type { BoxProps } from "@mui/material";

import { LoadingFallback } from "@/components/LoadingFallback";
import theme from "@/theme";

const TabLinkBase = forwardRef<HTMLAnchorElement, BoxProps>(
  function TabLinkBase(props, ref) {
    return <Box ref={ref} component="a" {...props} />;
  },
);

const DimensionsTabLink = createLink(TabLinkBase);

export const Route = createFileRoute("/_auth/dimensions/$siteId")({
  component: RouteComponent,
  pendingComponent: LoadingFallback,
});

const TABS = [
  { label: "Dragon Tiles", segment: undefined, to: "/dimensions/$siteId" },
  {
    label: "Exchanges",
    segment: "exchanges",
    to: "/dimensions/$siteId/exchanges",
  },
  { label: "People", segment: "people", to: "/dimensions/$siteId/people" },
] as const;

function RouteComponent() {
  const { siteId } = Route.useParams();
  const location = useLocation();
  const basePath = `/dimensions/${siteId}`;

  const activeIndex = TABS.findIndex((tab) =>
    tab.segment
      ? location.pathname.endsWith(`/${tab.segment}`)
      : location.pathname === basePath || location.pathname === `${basePath}/`,
  );
  const resolvedIndex = activeIndex === -1 ? 0 : activeIndex;

  const containerRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });

  const updateIndicator = useCallback(() => {
    const container = containerRef.current;
    const activeEl = tabRefs.current[resolvedIndex];
    if (container && activeEl) {
      const containerRect = container.getBoundingClientRect();
      const activeRect = activeEl.getBoundingClientRect();
      setIndicator({
        left: activeRect.left - containerRect.left,
        width: activeRect.width,
      });
    }
  }, [resolvedIndex]);

  useEffect(() => {
    updateIndicator();
  }, [updateIndicator]);

  if (!siteId) return <div>Please select a site first</div>;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        backgroundColor: (themeArg) =>
          themeArg.palette.background["paper-elevation-1"],
      }}
    >
      <Box
        sx={{
          px: 4,
          pt: 4,
          pb: 3,
          display: "flex",
          alignItems: "center",
          gap: 3,
          flexWrap: "wrap",
          flexShrink: 0,
        }}
      >
        <Box
          ref={containerRef}
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            bgcolor: "background.paper-elevation-2",
            borderRadius: theme.shape.borderRadiusSm + "px",
            p: 3,
            height: 40,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "4px",
              left: `${indicator.left}px`,
              width: `${indicator.width}px`,
              height: 30,
              borderRadius: theme.shape.borderRadiusSm + "px",
              bgcolor: "common.white",
              transition: "left 0.25s ease, width 0.25s ease",
              pointerEvents: "none",
            }}
          />

          {TABS.map((tab, index) => {
            const isActive = index === resolvedIndex;

            return (
              <DimensionsTabLink
                key={tab.label}
                ref={(el: HTMLAnchorElement | null) => {
                  tabRefs.current[index] = el;
                }}
                to={tab.to}
                params={{ siteId }}
                sx={{
                  position: "relative",
                  zIndex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  px: 5,
                  height: 30,
                  borderRadius: theme.shape.borderRadiusSm + "px",
                  color: isActive ? "text.primary" : "text.secondary",
                  textDecoration: "none",
                  fontSize: 14,
                  fontWeight: 400,
                  lineHeight: 1.5,
                  whiteSpace: "nowrap",
                  transition: "color 0.2s ease",
                  "&:hover": {
                    color: "text.primary",
                  },
                }}
              >
                {tab.label}
              </DimensionsTabLink>
            );
          })}
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Outlet key={siteId} />
      </Box>
    </Box>
  );
}
